import {Injectable, OnDestroy} from '@angular/core';
import {AuthService} from '@services/auth.service';
import {HttpClient} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {environment} from '@environments/environment';
import {LogoutComponent} from '@dialogs/logout/logout.component';
import {SessionState} from './session.enum';
import {BroadcastChannelService} from '@services/broadcast.service';
import {UserActivityService} from '@services/user-activity.service';
import {SessionMessage} from '@services/session/session.interface';
import {from, fromEvent, mergeMap} from 'rxjs';
import {map} from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class SessionManagerService implements OnDestroy {
    static SESSION_GRACE_THRESHOLD = 60000; // in milliseconds (1 min)

    private debug = false; // set this if you want to see console logs at various stages

    private readonly INACTIVITY_THRESHOLD = 300000; // in milliseconds (5 mins)
    private readonly TOKEN_EXPIRED_MSG = 'Expired token';
    private readonly LOGOUT_URL = `${environment.login.externalogoutPage}`;

    // misc
    private existingTitle = document.title;

    // dependencies
    private broadcastService: BroadcastChannelService;
    private dialogLogoutComponent: any = null;

    // timers
    private timeRemainingInterval: any = null;

    // session details
    private currentState = SessionState.ACTIVE;
    private lastCheckedTime = Date.now();
    private sessionExpiry = Date.now() + environment.sessionTimeout;

    constructor(
        private authService: AuthService,
        private dialogService: MatDialog,
        private httpService: HttpClient,
        private userActivityService: UserActivityService,
    ) {
        this.broadcastService = new BroadcastChannelService();
    }

    init(): void {
        this.sessionExpiry =
            Number(this.getSessionExpiryFromCookie()) || this.sessionExpiry;

        if (this.debug) {
            const timeRemaining = this.getTimeRemaining();

            console.log('[EFE-SessionService.init] Initializing session manager...');
            console.log('[EFE-SessionService.init] Default configurations:', {
                timeRemaining_ms: timeRemaining,
                timeRemaining_s: Math.ceil(timeRemaining / 1000),
                sessionExpiry_ms: this.sessionExpiry,
                sessionExpiry_s: Math.ceil(this.sessionExpiry / 1000),
                sessionExpiry_date: new Date(this.sessionExpiry).toISOString(),
                currentTime_date: new Date(Date.now()).toISOString(),
            });
        }

        this.verify(true);

        if (!this.authService.isLoggedIn()) {
            if (this.debug) {
                console.log('[EFE-SessionService.init] User is not logged in.');
            }

            this.expire();
        }

        this.initBroadcasting();
        this.initUserActivityTracking();
        this.restart();
    }

    private active(broadcast = true): void {
        if (this.currentState === SessionState.ACTIVE) {
            return;
        }

        this.setState(SessionState.ACTIVE, broadcast);

        this.dialogLogoutComponent?.close();
    }

    private broadcast(message: SessionMessage): void {
        this.broadcastService.broadcast(message);
    }

    /**
     * Check the time remaining before the session expires
     */
    private checkTimeRemaining(): void {
        if (this.dialogLogoutComponent) {
            return;
        }

        const timeRemaining = this.getTimeRemaining();

        if (this.debug) {
            console.log(
                '[EFE-SessionManager.checkTimeRemaining] Checking time remaining...',
                {
                    currentState: this.currentState,
                    timeRemaining_ms: timeRemaining,
                    timeRemaining_s: Math.ceil(timeRemaining / 1000),
                    sessionExpiry_ms: this.sessionExpiry,
                    sessionExpiry_s: Math.ceil(this.sessionExpiry / 1000),
                    sessionExpiry_date: new Date(this.sessionExpiry).toISOString(),
                    currentTime_date: new Date(Date.now()).toISOString(),
                },
            );
        }

        if (timeRemaining <= 0) {
            this.expire();
        } else if (this.withinGracePeriod()) {
            this.expiring();
        }
    }

    private compareCookieSessionValue(): void {
        const cookieSessionExpiry = this.getSessionExpiryFromCookie();

        if (cookieSessionExpiry <= this.sessionExpiry) {
            return;
        }

        this.sessionExpiry = cookieSessionExpiry;
    }

    private expire(broadcast = true): void {
        clearInterval(this.timeRemainingInterval);

        this.setState(SessionState.EXPIRED, broadcast);

        if (!this.dialogLogoutComponent) {
            this.exposeWarningDialog();
        }
    }

    private expiring(broadcast: boolean = true): void {
        if (this.debug) {
            const timeRemaining = this.getTimeRemaining();
            console.log(
                '[EFE-SessionManager.checkTimeRemaining] [!!!EXPIRING!!!] Time Remaining:',
                {timeRemaining},
            );
        }

        this.setState(SessionState.EXPIRING, broadcast);
        this.exposeWarningDialog();
    }

    private exposeWarningDialog(timeRemaining: number | undefined = 0): void {
        if (this.dialogLogoutComponent) {
            return;
        }

        const countdown = Math.max(0, timeRemaining || this.getTimeRemaining());

        this.dialogLogoutComponent = this.dialogService.open(LogoutComponent, {
            disableClose: true,
            width: '400px',
            height: 'auto',
            data: {
                message: `Your session will expire in ${countdown} seconds.`,
                countdown,
            },
        });

        this.dialogLogoutComponent.afterClosed().subscribe((done: boolean) => {
            if (this.debug) {
                console.log(
                    '[EFE-SessionManager.exposeWarningDialog] Dialog closed.',
                    done,
                );
            }

            this.dialogLogoutComponent = null;
            document.title = this.existingTitle;

            if (done) {
                this.authService.logout();
            } else {
                this.restart();
            }
        });
    }

    private getSessionExpiryFromCookie(): number {
        return this.authService.getSessionExpiry() * 1000;
    }

    getTimeRemaining(): number {
        this.compareCookieSessionValue();

        return Math.max(0, this.sessionExpiry - Date.now());
    }

    /**
     * Handle incoming messages from the BroadcastChannel
     */
    private handleEvent(event: any): void {
        const {status, sessionExpiry}: SessionMessage = event ?? {};

        if (!status) {
            return;
        }

        if (this.debug) {
            console.log('[EFE-SessionManager.handleBroadcastMsg] Received message:', {
                status,
                sessionExpiry,
            });
        }

        switch (status) {
            case SessionState.ACTIVE:
                this.active(false);

                break;
            case SessionState.EXPIRED:
                this.expire(false);

                break;
            case SessionState.EXPIRING:
                this.expiring(false);

                break;
        }
    }

    private initBroadcasting(): void {
        if (this.debug) {
            console.log('[EFE-SessionManager] Setting up BroadcastChannel...');
        }

        this.broadcastService
        .init('idle-activity')
        .listen(this.handleEvent.bind(this));

        this.broadcast({
            status: this.currentState,
            sessionExpiry: this.sessionExpiry,
        });
    }

    private initUserActivityTracking(): void {
        this.userActivityService.init(() => {
            if (this.getTimeRemaining() <= 0) {
                this.expire();

                return;
            }

            if (this.dialogLogoutComponent) {
                return;
            }

            if (this.withinGracePeriod()) {
                this.refresh();
            }

            if (this.lastCheckedTime >= this.INACTIVITY_THRESHOLD) {
                this.verify(true);
            }
        });

        from(['visibilitychange'])
        .pipe(
            mergeMap((eventName) =>
                fromEvent(document, eventName).pipe(
                    map((evt) => {
                        return {
                            name: eventName,
                            evt,
                        };
                    }),
                ),
            ),
        )
        .subscribe({
            next: (data) => {
                if (this.debug) {
                    console.log(
                        '[EFE-SessionManager] Visibility changed:',
                        document.hidden ? 'hidden' : 'visible',
                    );
                }

                const timeElapsed = Date.now() - this.lastCheckedTime;

                if (!document.hidden && timeElapsed > this.INACTIVITY_THRESHOLD) {
                    this.verify();
                }
            },
        });
    }

    public logout(): void {
        document.title = 'Quantivate';

        const queryParams = window.location.href;
        window.location.href = `${this.LOGOUT_URL}&destination=${encodeURIComponent(queryParams)}`;
    }

    public refresh(): void {
        if (this.currentState === SessionState.REFRESHING) {
            return;
        }

        if (this.debug) {
            console.log(
                '[EFE-SessionService.refresh]  Sending refresh (keep alive) request...',
            );
        }

        const previousState = this.currentState;
        this.lastCheckedTime = Date.now();
        this.setState(SessionState.REFRESHING);

        this.authService.refreshSession().subscribe({
            next: (response: any) => {
                if (this.debug) {
                    console.log(
                        '[EFE-SessionService.refresh] Refreshed (kept alive).',
                        response,
                    );
                }

                const refreshSessionExpiry = Number(response?.session_expiry);

                if (refreshSessionExpiry > this.sessionExpiry) {
                    this.sessionExpiry = refreshSessionExpiry * 1000;
                }

                this.active();
                this.restart();
            },
            error: (error) => {
                if (this.debug) {
                    console.log(
                        '[EFE-SessionService.refresh] Session refresh failed:',
                        error,
                    );
                }

                if (this.withinGracePeriod()) {
                    this.expiring();
                } else {
                    this.setState(previousState);
                }

                this.restart();
            },
        });
    }

    restart(): void {
        // Clear the interval if it exists
        clearInterval(this.timeRemainingInterval);

        this.checkTimeRemaining();
        this.timeRemainingInterval = setInterval(
            () => this.checkTimeRemaining(),
            1000,
        );
    }

    private setState(status: SessionState, broadcast: boolean = true): void {
        if (this.debug) {
            console.log('[EFE-SessionManager.setState] Setting state:', {
                previousState: this.currentState,
                newState: status,
                broadcast,
            });
        }

        this.currentState = status;

        if (broadcast) {
            this.broadcast({
                status,
                sessionExpiry: this.sessionExpiry,
            });
        }
    }

    verify(refresh: boolean = false): void {
        if (
            this.currentState === SessionState.REFRESHING ||
            this.currentState === SessionState.VERIFYING
        ) {
            return;
        }

        if (this.debug) {
            console.log('[EFE-SessionService.verify] Verifying session...');
        }

        const previousState = this.currentState;
        this.lastCheckedTime = Date.now();
        this.setState(SessionState.VERIFYING);

        this.httpService
        .get(`${environment.platformUrls.api}/sessions/verify`)
        .subscribe({
            next: (response: any) => {
                const verifySessionExpiry = Number(response?.session_expiry);

                if (verifySessionExpiry > this.sessionExpiry) {
                    this.sessionExpiry = verifySessionExpiry * 1000;
                }

                if (this.debug) {
                    console.log(
                        '[EFE-SessionService.verify] Session verified:',
                        response,
                    );
                }

                const timeRemaining = this.getTimeRemaining();

                if (timeRemaining <= 0) {
                    this.expire();
                } else if (this.withinGracePeriod()) {
                    this.expiring();
                } else if (
                    !this.withinGracePeriod() &&
                    timeRemaining > SessionManagerService.SESSION_GRACE_THRESHOLD
                ) {
                    this.active();
                } else {
                    this.setState(previousState);
                }
            },
            error: (error) => {
                if (this.debug) {
                    console.log(
                        '[EFE-SessionService.verify] Session verification failed',
                        refresh,
                        error,
                    );
                }

                // refresh the access token with the refresh token if the token discernibly expired
                if (
                    (error.status === 401 || error.status === 403) &&
                    refresh &&
                    error.error?.message?.includes(this.TOKEN_EXPIRED_MSG)
                ) {
                    this.refresh();
                }
            },
        });
    }

    public withinGracePeriod(): boolean {
        const timeRemaining = this.getTimeRemaining();

        return (
            timeRemaining > 0 &&
            timeRemaining <= SessionManagerService.SESSION_GRACE_THRESHOLD
        );
    }

    ngOnDestroy(): void {
        document.title = this.existingTitle;
        this.broadcastService.close();
        this.userActivityService.clear();
    }
}
